import { useCallback, useEffect, useState } from "react";
import {
  fetchAdsSocialMappings,
  updateAdsLocationSocialMapping,
  updateAdsSocialDefaults,
  type AdsLocationSocialMapping,
} from "@/lib/adsAssistantClient";

type Props = { scopedOrgId: string | null };

type OrgDraft = {
  social_default_facebook_page_url: string;
  social_default_facebook_page_id: string;
  social_default_google_business_url: string;
  social_default_google_business_location_id: string;
};

const EMPTY_ORG: OrgDraft = {
  social_default_facebook_page_url: "",
  social_default_facebook_page_id: "",
  social_default_google_business_url: "",
  social_default_google_business_location_id: "",
};

function toText(v: string | null | undefined): string {
  return typeof v === "string" ? v : "";
}

export function AdsAssistantSocialMappingsSection({ scopedOrgId }: Props) {
  const [loading, setLoading] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);
  const [savingLocId, setSavingLocId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [orgDraft, setOrgDraft] = useState<OrgDraft>(EMPTY_ORG);
  const [locations, setLocations] = useState<AdsLocationSocialMapping[]>([]);

  const load = useCallback(async () => {
    if (!scopedOrgId) return;
    setLoading(true);
    setError(null);
    const r = await fetchAdsSocialMappings();
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setCanEdit(false);
      setLocations([]);
      return;
    }
    setCanEdit(!!r.data.can_edit);
    setOrgDraft({
      social_default_facebook_page_url: toText(r.data.organization?.social_default_facebook_page_url),
      social_default_facebook_page_id: toText(r.data.organization?.social_default_facebook_page_id),
      social_default_google_business_url: toText(r.data.organization?.social_default_google_business_url),
      social_default_google_business_location_id: toText(r.data.organization?.social_default_google_business_location_id),
    });
    setLocations(r.data.locations ?? []);
  }, [scopedOrgId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!banner) return;
    const id = window.setTimeout(() => setBanner(null), 2200);
    return () => window.clearTimeout(id);
  }, [banner]);

  async function saveOrgDefaults() {
    setSavingOrg(true);
    setError(null);
    const r = await updateAdsSocialDefaults({
      social_default_facebook_page_url: orgDraft.social_default_facebook_page_url || null,
      social_default_facebook_page_id: orgDraft.social_default_facebook_page_id || null,
      social_default_google_business_url: orgDraft.social_default_google_business_url || null,
      social_default_google_business_location_id: orgDraft.social_default_google_business_location_id || null,
    });
    setSavingOrg(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setBanner("Org defaults saved.");
    await load();
  }

  async function saveLocation(row: AdsLocationSocialMapping) {
    setSavingLocId(row.id);
    setError(null);
    const r = await updateAdsLocationSocialMapping(row.id, {
      social_facebook_page_url: row.social_facebook_page_url || null,
      social_facebook_page_id: row.social_facebook_page_id || null,
      social_google_business_url: row.social_google_business_url || null,
      social_google_business_location_id: row.social_google_business_location_id || null,
    });
    setSavingLocId(null);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    setBanner(`Saved ${row.name}.`);
    await load();
  }

  if (!scopedOrgId) return null;

  return (
    <details className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/60 p-3">
      <summary className="cursor-pointer list-none text-sm font-semibold text-gray-900 dark:text-gray-100 [&::-webkit-details-marker]:hidden">
        Social mappings (location 1:1 + org fallback)
      </summary>

      <div className="mt-3 space-y-3">
        <p className="text-xs text-gray-600 dark:text-gray-300">
          Each location can map to Facebook/Google directly. If blank, imports can fall back to org defaults. Org-level
          defaults are restricted to marketing/admin/owner (and platform admin/dev).
        </p>
        {banner ? <p className="text-xs text-emerald-700">{banner}</p> : null}
        {error ? <p className="text-xs text-amber-800">{error}</p> : null}

        <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 p-3">
          <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-100">Org fallback (no location mapping)</h4>
          {!canEdit ? (
            <p className="mt-1 text-xs text-amber-800">
              Read-only: editing org defaults requires marketing, admin, owner, or platform admin/dev.
            </p>
          ) : null}
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            <input
              className="rounded border border-gray-200 px-2 py-1.5 text-xs"
              placeholder="Default Facebook Page URL"
              value={orgDraft.social_default_facebook_page_url}
              onChange={(e) => setOrgDraft((s) => ({ ...s, social_default_facebook_page_url: e.target.value }))}
              disabled={!canEdit || savingOrg}
            />
            <input
              className="rounded border border-gray-200 px-2 py-1.5 text-xs"
              placeholder="Default Facebook Page ID"
              value={orgDraft.social_default_facebook_page_id}
              onChange={(e) => setOrgDraft((s) => ({ ...s, social_default_facebook_page_id: e.target.value }))}
              disabled={!canEdit || savingOrg}
            />
            <input
              className="rounded border border-gray-200 px-2 py-1.5 text-xs"
              placeholder="Default Google Business URL"
              value={orgDraft.social_default_google_business_url}
              onChange={(e) => setOrgDraft((s) => ({ ...s, social_default_google_business_url: e.target.value }))}
              disabled={!canEdit || savingOrg}
            />
            <input
              className="rounded border border-gray-200 px-2 py-1.5 text-xs"
              placeholder="Default Google Business Location ID"
              value={orgDraft.social_default_google_business_location_id}
              onChange={(e) => setOrgDraft((s) => ({ ...s, social_default_google_business_location_id: e.target.value }))}
              disabled={!canEdit || savingOrg}
            />
          </div>
          <button
            type="button"
            className="mt-2 rounded bg-indigo-600 px-3 py-1.5 text-xs text-white disabled:opacity-50"
            onClick={() => void saveOrgDefaults()}
            disabled={!canEdit || savingOrg}
          >
            {savingOrg ? "Saving..." : "Save org defaults"}
          </button>
        </section>

        <section className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 p-3">
          <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-100">Per-location mappings</h4>
          {loading ? <p className="mt-1 text-xs text-gray-500">Loading...</p> : null}
          <div className="mt-2 space-y-2">
            {locations.map((row) => (
              <div key={row.id} className="rounded border border-gray-200 dark:border-gray-700 p-2">
                <p className="text-xs font-medium text-gray-900 dark:text-gray-100">{row.name}</p>
                <div className="mt-1 grid gap-1.5 sm:grid-cols-2">
                  <input
                    className="rounded border border-gray-200 px-2 py-1 text-xs"
                    placeholder="Facebook Page URL"
                    value={toText(row.social_facebook_page_url)}
                    onChange={(e) =>
                      setLocations((prev) =>
                        prev.map((l) => (l.id === row.id ? { ...l, social_facebook_page_url: e.target.value } : l))
                      )
                    }
                    disabled={!canEdit || savingLocId === row.id}
                  />
                  <input
                    className="rounded border border-gray-200 px-2 py-1 text-xs"
                    placeholder="Facebook Page ID"
                    value={toText(row.social_facebook_page_id)}
                    onChange={(e) =>
                      setLocations((prev) =>
                        prev.map((l) => (l.id === row.id ? { ...l, social_facebook_page_id: e.target.value } : l))
                      )
                    }
                    disabled={!canEdit || savingLocId === row.id}
                  />
                  <input
                    className="rounded border border-gray-200 px-2 py-1 text-xs"
                    placeholder="Google Business URL"
                    value={toText(row.social_google_business_url)}
                    onChange={(e) =>
                      setLocations((prev) =>
                        prev.map((l) => (l.id === row.id ? { ...l, social_google_business_url: e.target.value } : l))
                      )
                    }
                    disabled={!canEdit || savingLocId === row.id}
                  />
                  <input
                    className="rounded border border-gray-200 px-2 py-1 text-xs"
                    placeholder="Google Business Location ID"
                    value={toText(row.social_google_business_location_id)}
                    onChange={(e) =>
                      setLocations((prev) =>
                        prev.map((l) =>
                          l.id === row.id ? { ...l, social_google_business_location_id: e.target.value } : l
                        )
                      )
                    }
                    disabled={!canEdit || savingLocId === row.id}
                  />
                </div>
                <button
                  type="button"
                  className="mt-2 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 disabled:opacity-50"
                  onClick={() => void saveLocation(row)}
                  disabled={!canEdit || savingLocId === row.id}
                >
                  {savingLocId === row.id ? "Saving..." : "Save location mapping"}
                </button>
              </div>
            ))}
            {!locations.length && !loading ? <p className="text-xs text-gray-500">No locations found.</p> : null}
          </div>
        </section>
      </div>
    </details>
  );
}
